import { Component, OnInit } from '@angular/core';
import { FormGroup, FormControl, Validators } from '@angular/forms';
import { Router } from '@angular/router';
import { AuthenticationService } from '../authentication.service';

@Component({
  selector: 'app-forgot-password',
  templateUrl: './forgot-password.component.html',
  styleUrls: ['./forgot-password.component.scss']
})
export class ForgotPasswordComponent implements OnInit {
  recoverPasswordForm: FormGroup;
  successMessage: string | null = null;
  errorMessage: string | null = null;
  emailLinkInfo: string | null = null;

  constructor(private authService: AuthenticationService, private router: Router) {}

  ngOnInit() {
    this.recoverPasswordForm = new FormGroup({
      email: new FormControl('', [Validators.required, Validators.email])
    });
    void this.bootstrapEmailLinkLanding();
  }

  private async bootstrapEmailLinkLanding(): Promise<void> {
    const linkResult = await this.authService.tryCompleteEmailLinkSignInFromCurrentUrl();
    if (linkResult.status === 'signed_in' || linkResult.status === 'linked') {
      await this.router.navigateByUrl(linkResult.postNavigatePath);
      return;
    }
    if (linkResult.status === 'error') {
      this.errorMessage = linkResult.message;
    }
  }

  get isLoggedIn(): boolean {
    return this.authService.isLoggedIn;
  }

  async onRecoverPassword() {
    const email = this.recoverPasswordForm.value.email;
    this.emailLinkInfo = null;
    try {
      await this.authService.resetPassword(email);
      this.successMessage = 'Recovery Email Sent.';
      this.errorMessage = null;
    } catch (err) {
      console.error('Password Reset Failed:', err);
      this.errorMessage = 'Failed To Send Reset Email. Please Try Again.';
      this.successMessage = null;
    }
  }

  async onEmailLinkRecovery(): Promise<void> {
    const emailControl = this.recoverPasswordForm.get('email');
    emailControl?.markAsTouched();
    if (!emailControl?.valid) {
      this.errorMessage = 'Enter a valid email address.';
      this.successMessage = null;
      this.emailLinkInfo = null;
      return;
    }
    const email = String(emailControl.value || '').trim();
    this.errorMessage = null;
    this.successMessage = null;
    this.emailLinkInfo = null;
    try {
      await this.authService.sendEmailSignInLink(email, {
        mode: 'signin',
        continuePath: '/auth/forgot-password',
        postAuthNavigatePath: '/ai/clarity'
      });
      this.emailLinkInfo =
        'If you use passwordless sign-in, check your email for the link. After signing in you can change your password from account settings.';
    } catch (err: any) {
      console.error('Email link send failed:', err);
      this.errorMessage = err?.message || 'Could not send sign-in link. Please try again.';
    }
  }

  async onLinkEmailForSignedInUser(): Promise<void> {
    const emailControl = this.recoverPasswordForm.get('email');
    emailControl?.markAsTouched();
    if (!emailControl?.valid) {
      this.errorMessage = 'Enter the email address to link.';
      this.successMessage = null;
      this.emailLinkInfo = null;
      return;
    }
    const email = String(emailControl.value || '').trim();
    this.errorMessage = null;
    this.successMessage = null;
    this.emailLinkInfo = null;
    try {
      await this.authService.sendEmailSignInLink(email, {
        mode: 'link',
        continuePath: '/auth/forgot-password',
        postAuthNavigatePath: '/ai/clarity'
      });
      this.emailLinkInfo =
        'Open the link in this browser while you stay signed in here so we can attach email sign-in to your current account.';
    } catch (err: any) {
      console.error('Link email send failed:', err);
      this.errorMessage = err?.message || 'Could not send verification link. Please try again.';
    }
  }
}
